import React, { FC, ForwardedRef, useCallback, useContext, useEffect, useReducer, useState } from 'react'
import {
    BlueConicRecommendedNotificationContext,
    BlueConicRecommendedNotificationDispatchContext,
    blueConicRecommendedNotificationReducer,
} from './reducer'
import {
    BlueConicRecommendationConfiguration,
    BlueConicRecommendedNotificationParams,
} from '@pushly/models/lib/structs/journeys'
import { BlueConicConfigurationEditor } from './configuration/blueconic-configuration-editor'
import { NodeEditorRef } from '../../../types/journey-nodes'
import { Button } from 'antd'
import { PlusCircleFilled, PlusOutlined } from '@ant-design/icons'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import { DomainDto } from '../../../../../dtos/domain'
import {
    defaultBlueConicConfigurationValue,
    INDEX_DISPLAY_DEFAULT_ID,
    MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED,
} from './constants'
import './blueconic-recommendation-editors.scss'
import {
    EditableViewContext,
    EditableViewWrapper,
} from '@pushly/aqe/lib/components/hoc/editable-view/editable-view-context'

type BlueConicRecommendationEditorProps = {
    domain: DomainDto
    integration_id: number
    blueConicRecommendation: BlueConicRecommendedNotificationParams
    onChange: (change: BlueConicRecommendedNotificationParams) => void
    ref: ForwardedRef<NodeEditorRef>
    readonly?: boolean
}

export const BlueConicRecommendationEditor: FC<BlueConicRecommendationEditorProps> = ({
    onChange,
    domain,
    blueConicRecommendation,
    readonly,
}) => {
    const [context, dispatch] = useReducer(blueConicRecommendedNotificationReducer, {
        source: blueConicRecommendation,
        configs: new Map(
            blueConicRecommendation.recommendation_config?.map((config) => [getRandId(), config]) ?? [
                [
                    getRandId(),
                    BlueConicRecommendationConfiguration.parseJsonObject({
                        id: INDEX_DISPLAY_DEFAULT_ID[0],
                        boosts: [],
                        filters: { and: [] },
                        count: 1,
                    }),
                ],
            ],
        ),
    })

    const [latestAdd, setLatestAdd] = useState<string | null>(null)

    useEffect(() => {
        onChange(context.source.clone({ recommendation_config: Array.from(context.configs.values()) }))
    }, [context])

    useEffect(() => {
        if (latestAdd) {
            const newConfigEl = document.getElementById(latestAdd)
            if (newConfigEl) {
                newConfigEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
        }
    }, [latestAdd])

    const handleAddConfig = useCallback(
        (config?: BlueConicRecommendationConfiguration) => {
            // 6 configs is the max allowed currently
            if (context.configs.size >= MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED) return

            const newConfigRecordId = getRandId()

            dispatch({
                type: 'add-configuration',
                recordId: newConfigRecordId,
                configuration: BlueConicRecommendationConfiguration.from({
                    ...defaultBlueConicConfigurationValue,
                    ...config,
                    id: INDEX_DISPLAY_DEFAULT_ID[context.configs.size],
                    count: 1,
                }),
            })

            setLatestAdd(newConfigRecordId)
        },
        [context.configs.size],
    )

    return (
        <div id="blueconic-recommendation-notification-editor">
            <EditableViewWrapper mode={readonly ? 'view' : 'edit'}>
                <BlueConicRecommendedNotificationContext.Provider value={context}>
                    <BlueConicRecommendedNotificationDispatchContext.Provider value={dispatch}>
                        {Array.from(context.configs.entries()).map(([key, configuration]) => (
                            <BlueConicConfigurationEditor
                                domain={domain}
                                key={key}
                                configuration={{ recordId: key, configuration }}
                                handleAdd={handleAddConfig}
                            />
                        ))}

                        {readonly || context.configs.size >= MAX_BLUECONIC_RECOMMENDATION_CONIGS_ALLOWED ? null : (
                            <Button
                                type="primary"
                                className="add-configuration-button"
                                size="small"
                                onClick={() => handleAddConfig()}
                                icon={<PlusCircleFilled />}
                            >
                                Add Configuration
                            </Button>
                        )}
                    </BlueConicRecommendedNotificationDispatchContext.Provider>
                </BlueConicRecommendedNotificationContext.Provider>
            </EditableViewWrapper>
        </div>
    )
}
